import React, { useMemo, useState } from 'react';
import { Box, Typography, Select, MenuItem, Snackbar, Alert, Tab, Tabs } from '@mui/material';
import { Header } from '../../components';
import { customStyles } from '../../Utils/utils';
import BillingPanel from '../dashboard/BillingPanel';
import ClinicRatesTable from './ClinicRatesTable';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Money — the invoice register, payments and receivables.

   Separated from the Super Admin dashboard on purpose. The dashboard answers
   "how is the business performing"; this answers "who owes us money and what
   has arrived". They are different jobs done by different people at different
   times, and chasing a debt should not require scrolling past charts.
   ════════════════════════════════════════════════════════════════════════ */

const Billing: React.FC = () => {
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | 'all'>('all');
  const [tab, setTab] = useState<'rates' | 'register'>('rates');
  // Bumped whenever one tab changes something the other displays — raising an invoice
  // on the rates tab has to show up in the register without a page reload.
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 2021 + 1 }, (_, i) => 2021 + i).reverse();
  }, []);

  return (
    <>
      <section
        className="layoutContainer"
        style={customStyles.layoutBackground}
      >
        <div className="layoutGrid">
          <div className="page page--fullBody">
            <Header />
            <div className="page__body">
              <Box sx={{ p: '20px 30px 40px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: '4px',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}
                    >
                      Money
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: T.ink3, mt: '3px' }}>
                      Invoices raised, payments received and amounts outstanding
                    </Typography>
                  </Box>
                  <Select
                    size="small"
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                    sx={{
                      minWidth: 100,
                      color: T.ink,
                      '.MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                    }}
                  >
                    {years.map(y => (
                      <MenuItem
                        key={y}
                        value={y}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{
                    mt: '18px',
                    borderBottom: `1px solid ${T.line}`,
                    '.MuiTab-root': {
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                      color: T.ink3,
                    },
                    '.Mui-selected': { color: `${T.teal} !important` },
                    '.MuiTabs-indicator': { backgroundColor: T.teal },
                  }}
                >
                  <Tab
                    value="rates"
                    label="Clinics & rates"
                  />
                  <Tab
                    value="register"
                    label="Invoice register"
                  />
                </Tabs>

                {tab === 'rates' ? (
                  <Box sx={{ mt: '16px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: '14px',
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, color: T.ink3 }}>
                        Volume shown is for the selected period. Rates and schedules apply from now
                        on, whatever period is displayed.
                      </Typography>
                      <Select
                        size="small"
                        value={month}
                        onChange={e => setMonth(e.target.value as number | 'all')}
                        sx={{
                          minWidth: 140,
                          color: T.ink,
                          '.MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                        }}
                      >
                        <MenuItem value="all">Whole year</MenuItem>
                        {[
                          'Jan',
                          'Feb',
                          'Mar',
                          'Apr',
                          'May',
                          'Jun',
                          'Jul',
                          'Aug',
                          'Sep',
                          'Oct',
                          'Nov',
                          'Dec',
                        ].map((m, i) => (
                          <MenuItem
                            key={m}
                            value={i + 1}
                          >
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <ClinicRatesTable
                      year={year}
                      month={month}
                      onChanged={() => setRefreshKey(k => k + 1)}
                      notify={(msg, sev) => setToast({ msg, sev })}
                    />
                  </Box>
                ) : (
                  <BillingPanel
                    key={refreshKey}
                    year={year}
                    notify={(msg, sev) => setToast({ msg, sev })}
                  />
                )}
              </Box>
            </div>
          </div>
        </div>
      </section>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.sev || 'success'}
          onClose={() => setToast(null)}
          variant="filled"
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Billing;
